import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "yewujiang_favorites";
const CHANGE_EVENT = "yewujiang-favorites-change";

export const FAVORITES_KEY = STORAGE_KEY;

export function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string");
  } catch {
    return [];
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readFavorites());
    const handler = () => setFavorites(readFavorites());
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  const toggle = useCallback((id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [id, ...prev];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage failures in demo mode
      }
      window.dispatchEvent(new Event(CHANGE_EVENT));
      return next;
    });
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  const clear = useCallback(() => {
    setFavorites([]);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    } catch {
      // ignore storage failures in demo mode
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { favorites, toggle, isFavorite, clear };
}